import { flattenClassShapes } from './Import/CanvasShapeFlattener';

/**
 * Computes a presentation-only derived view of shapes/connectors with
 * single-implementer interfaces hidden, replaced by a direct connector
 * "via <Interface>" from dependent to implementer. Pure function — never
 * mutates the real shapes/connectors (umlCanvasEngine.shapes/.connectors),
 * that's the whole point of keeping data and view separate. When
 * hideInterfaces is false, returns the same references unchanged.
 * @param {object[]} shapes - umlCanvasEngine.shapes (top-level)
 * @param {object[]} connectors - umlCanvasEngine.connectors
 * @param {boolean} hideInterfaces
 * @returns {{visibleShapes: object[], visibleConnectors: object[]}}
 */
export function resolveInterfaceReduction(shapes, connectors, viewConfig) {
    if (!viewConfig.hideInterfaces) {
        return { visibleShapes: shapes, visibleConnectors: connectors };
    }

    const flat = flattenClassShapes(shapes);
    const reducible = findReducibleInterfaces(flat);

    return {
        visibleShapes: filterHiddenShapes(shapes, reducible),
        visibleConnectors: rewireConnectors(connectors, reducible, viewConfig.derivedLabelOffsets || {}),
    };
}

/**
 * Interfaces with EXACTLY one implementer among the current shapes — the
 * only safe case to collapse. 2+ implementers would hide WHICH one a
 * dependent actually needs (see Development.Patterns rk=2b9b9459c0644b7395953748696b2468),
 * so those interfaces stay visible as-is.
 * @param {object[]} flatShapes
 * @returns {Map<string, {implementer: object, name: string}>} interface shape id -> implementer + interface name
 */
function findReducibleInterfaces(flatShapes) {
    const interfaces = flatShapes.filter((s) => s.caption?.stereotype === 'interface');
    const result = new Map();

    interfaces.forEach((iface) => {
        const ifaceName = iface.caption?.text;
        // Only CONCRETE classes count as "the implementer" — an interface-to-interface
        // `extends` (A extends B) also satisfies inherits.includes(name), but B's "implementer"
        // would then be another interface, possibly itself hidden, which produced a connector
        // rewired onto a shape that no longer renders (bug found 2026-07-11, hanging line onto
        // an invisible ISimpleTickerTypeHandler via IEnvelopeTypeEventHandler).
        const implementers = flatShapes.filter((s) => s.caption?.stereotype !== 'interface' && (s.inherits || []).includes(ifaceName));
        if (implementers.length === 1) {
            result.set(iface.id, { implementer: implementers[0], name: ifaceName });
        }
    });

    return result;
}

/**
 * Rebuilds the shapes tree without the reducible interfaces — new arrays
 * (incl. new package.children arrays where needed), same shape object
 * references for everything that stays, so nothing is deep-cloned/mutated.
 * @param {object[]} shapes
 * @param {Map<string, object>} reducible
 * @returns {object[]}
 */
function filterHiddenShapes(shapes, reducible) {
    return shapes
        .filter((shape) => !reducible.has(shape.id))
        .map((shape) => {
            if (!shape.children) return shape;
            const filteredChildren = filterHiddenShapes(shape.children, reducible);
            if (filteredChildren === shape.children) return shape;
            // Object.create + assign instead of {...shape} — a plain spread loses the
            // prototype chain, breaking every `instanceof UmlPackageShape` check
            // downstream (UmlCanvasHelpers.renderShape), which then renders the WHOLE
            // package (and everything nested in it) as null instead of just hiding
            // the one reduced interface (bug found 2026-07-11).
            const clone = Object.create(Object.getPrototypeOf(shape));
            Object.assign(clone, shape, { children: filteredChildren });
            return clone;
        });
}

/**
 * Drops the implementer->interface inheritance connector (now implied by the
 * direct line), and rewires any other connector touching a reducible
 * interface to point at the implementer directly, tagged with a
 * derivedLabel + derivedLabelOffset for UmlConnectorPath to render/drag
 * ("via <Interface>").
 * @param {object[]} connectors
 * @param {Map<string, {implementer: object, name: string}>} reducible
 * @param {object} offsets - viewConfig.derivedLabelOffsets, keyed by real connector id
 * @returns {object[]}
 */
function rewireConnectors(connectors, reducible, offsets) {
    return connectors
        .filter((c) => !(c.kind === 'inheritance' && reducible.has(c.toShapeId)))
        .map((c) => {
            const fromEntry = reducible.get(c.fromShapeId);
            const toEntry = reducible.get(c.toShapeId);
            if (!fromEntry && !toEntry) return c;

            const label = fromEntry ? `via ${fromEntry.name}` : `via ${toEntry.name}`;
            // Object.create + assign instead of {...c} — zelfde reden als filterHiddenShapes
            // hierboven: plain spread verliest de prototype-chain, wat hier connector.select()/
            // .selectAnchor() zou breken zodra je een omgeleide "via <Interface>"-connector aanklikt.
            const clone = Object.create(Object.getPrototypeOf(c));
            Object.assign(clone, c, {
                fromShapeId: fromEntry ? fromEntry.implementer.id : c.fromShapeId,
                toShapeId: toEntry ? toEntry.implementer.id : c.toShapeId,
                derivedLabel: label,
                derivedLabelOffset: offsets[c.id] || { x: 0, y: -12 }, // default: iets boven de lijn i.p.v. er bovenop
            });
            return clone;
        });
}
